/*
 * tunnel-video.js — túnel de fondo para MÓVIL/TABLET: un <video> scrubbeado por scroll.
 * ============================================================================
 * El túnel de escritorio (236 JPEGs 1080p en #cinema) NO cabe en la memoria de un teléfono (~2 GB
 * decodificados). Aquí, en su lugar, un <video> (public/video/tunnel.mp4, 720p) se SCRUBBEA por scroll
 * seteando `currentTime` según el avance de la página: el navegador decodifica bajo demanda (memoria
 * baja) y el túnel solo se mueve al scrollear (más calmado y menos batería que un loop). Requiere
 * keyframes densos en el mp4 (se transcodificó con gopsize bajo) para que el seek sea fluido.
 *
 * iOS: para poder scrubbear hay que "activar" el decodificador; con muted + playsinline el autoplay
 * está permitido sin gesto, así que arrancamos y pausamos de inmediato.
 */

export function mountTunnelVideoScrub({ url } = {}) {
  if (!url) return () => {};
  const video = document.createElement('video');
  video.className = 'tunnel-video';
  video.src = url;
  video.muted = true; video.defaultMuted = true;
  video.playsInline = true;
  video.loop = false;
  video.preload = 'auto';
  video.setAttribute('playsinline', '');
  video.setAttribute('webkit-playsinline', '');
  video.setAttribute('muted', '');
  video.setAttribute('aria-hidden', 'true');
  document.body.prepend(video); // z-index gobierna el apilado; va detrás del contenido (#cinema z0)

  let raf = null, running = true, dur = 0, activated = false;

  video.addEventListener('loadedmetadata', () => { dur = video.duration || 0; });

  // Activa el decodificador (muted autoplay permitido) y pausa: así se puede scrubbear por currentTime.
  function activate() {
    if (activated) return; activated = true;
    const p = video.play();
    if (p && typeof p.then === 'function') p.then(() => video.pause()).catch(() => { try { video.pause(); } catch (e) {} });
    else { try { video.pause(); } catch (e) {} }
    video.classList.add('is-live');
  }
  video.addEventListener('canplay', activate, { once: true });
  video.addEventListener('loadeddata', activate, { once: true });

  // Avance de la página 0→1 (no hay Lenis en móvil; scroll nativo).
  function progress() {
    const max = document.documentElement.scrollHeight - window.innerHeight;
    return max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
  }

  function loop() {
    if (!running) return;
    raf = requestAnimationFrame(loop);
    if (!dur) return;
    const target = progress() * dur;
    // solo re-seek si cambió apreciablemente y no hay un seek en curso (evita apilar seeks → jank)
    if (!video.seeking && Math.abs(video.currentTime - target) > 0.033) {
      try { video.currentTime = target; } catch (e) { /* aún no seekable */ }
    }
  }
  raf = requestAnimationFrame(loop);

  return () => {
    running = false;
    if (raf) cancelAnimationFrame(raf);
    try { video.pause(); } catch (e) {}
    video.removeAttribute('src');
    try { video.load(); } catch (e) {}
    video.remove();
  };
}
